import { Modal, Typography, Button, Alert, List } from 'antd';
import { InfoCircleOutlined, UserOutlined, FileTextOutlined } from '@ant-design/icons';
import { X } from 'lucide-react';
import { Tenant } from '@/features/superadmin/types';

const { Text, Paragraph } = Typography;

interface DeleteTenantDialogProps {
  open: boolean;
  onClose: () => void;
  onConfirm: () => void;
  tenant: Tenant | null;
  isDeleting: boolean;
}

export function DeleteTenantDialog({
  open,
  onClose,
  onConfirm,
  tenant,
  isDeleting
}: DeleteTenantDialogProps) {
  // Verificamos se há usuários ou contratos vinculados para mostrar um aviso
  const hasLinkedEntities = tenant &&
    ((tenant._count?.users || 0) > 0 || (tenant._count?.contratos || 0) > 0);

  return (
    <Modal
      open={open}
      onCancel={onClose}
      footer={null}
      closable={false}
      width={600}
      destroyOnHidden
      centered
      styles={{
        mask: { background: 'rgba(0, 0, 0, 0.45)' },
        body: { padding: 0, overflow: 'hidden' },
      }}
      zIndex={1000}
    >
      <button
        type="button"
        onClick={onClose}
        className="absolute top-4 right-4 z-50 p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-zinc-800 text-gray-500 dark:text-gray-400 transition"
        aria-label="Fechar"
        disabled={isDeleting}
      >
        <X className="w-5 h-5" />
      </button>

      <div className="flex flex-col h-auto bg-white dark:bg-zinc-900">
        <div className="bg-[#ff4d4f] dark:bg-red-900 text-white px-6 py-4 flex-shrink-0">
          <p className="text-[11px] text-white/50 uppercase tracking-wide mb-1">
            Ação Destrutiva
          </p>
          <h2 className="text-[15px] font-semibold">
            Excluir Empresa
          </h2>
        </div>

        <div className="px-6 py-6 flex-1 overflow-y-auto max-h-[70vh]">
          {hasLinkedEntities ? (
            <div className="space-y-4">
              <Alert
                message="Esta empresa não pode ser excluída no momento."
                description={
                  <div style={{ marginTop: 8 }}>
                    <Paragraph>
                      Existem registros vinculados a esta empresa que impedem sua remoção direta:
                    </Paragraph>
                    <List size="small" split={false} className="mt-2">
                      <List.Item>
                        <div className="flex items-center gap-2">
                          <UserOutlined className="text-blue-500" />
                          <Text>{tenant?._count?.users || 0} usuários vinculados</Text>
                        </div>
                      </List.Item>
                      <List.Item>
                        <div className="flex items-center gap-2">
                          <FileTextOutlined className="text-blue-500" />
                          <Text>{tenant?._count?.contratos || 0} contratos vinculados</Text>
                        </div>
                      </List.Item>
                    </List>
                    <Paragraph style={{ marginTop: 12, marginBottom: 0 }}>
                      Você deve primeiro mover ou excluir todos os usuários e contratos antes de excluir a empresa.
                    </Paragraph>
                  </div>
                }
                type="error"
                showIcon
              />
            </div>
          ) : (
            <div className="space-y-4">
              <div className="bg-red-50 dark:bg-red-950/30 p-4 rounded-lg border border-red-200 dark:border-red-900">
                <Paragraph style={{ margin: 0 }} className="text-gray-900 dark:text-white">
                  Você tem certeza que deseja excluir a empresa <strong>"{tenant?.nome}"</strong>?
                </Paragraph>
              </div>
              <Alert
                message="Atenção"
                description="Esta ação não pode ser desfeita e todos os dados relacionados (configurações, logs específicos) serão removidos permanentemente."
                type="warning"
                showIcon
                icon={<InfoCircleOutlined />}
              />
            </div>
          )}

          <div className="flex gap-3 mt-8 pt-4 border-t border-gray-200 dark:border-zinc-700">
            <Button onClick={onClose} style={{ flex: 1 }} disabled={isDeleting}>
              Cancelar
            </Button>
            {!hasLinkedEntities && (
              <Button
                type="primary"
                danger
                loading={isDeleting}
                onClick={onConfirm}
                style={{ flex: 1 }}
              >
                Excluir Empresa
              </Button>
            )}
          </div>
        </div>
      </div>
    </Modal>
  );
}